import React, { useState, JSX, useEffect } from 'react';
import {
  AssociateInfo,
  Enrollments,
  getDefaultFormData,
  NewJoinSessionData,
  NewMembershipFormData,
  PartnerEnrollments,
  ProductType,
} from '../../application/models/NewJoinSessionData';
import { useLoadingInfo } from '../../application/models/LoadingInfo';
import { IMembershipAppState, MembershipAppStateContext } from './MembershipAppState';
import {
  ErrorType,
  IAdditionalBenefits,
  IApiResponse,
  IDisclaimers,
  IGetNewBusinessCostRequest,
  IPaperlessOptOut,
  IPartnerIntegration,
  Pages,
  PaperlessOptOutType,
} from '@lib/types';
import { useHistory } from 'react-router-dom';
import { MembershipStatus } from '../../application/models/MembershipStatus';
import {
  ISubmitNewJoinFormDataResponse,
  IRegisterAccountResponse,
  IJoinConfirmationRequest,
  ISubmitNewJoinFormDataRequest,
  ICheckMembershipStatusRequest,
  IGetPaymentManifestRequest,
  IGetPaymentManifestResponse,
  IRegisterAccountRequest,
  IGetNewMembershipInfoResponse,
  IGetNewBusinessCostResponse,
  IGetMembershipContentResponse,
} from '../../api/contracts';
import { TokenResponse } from '../../application/models/TokenStatus';
import { UtagHelper, UtagType } from '../../utils/UtagHelper';
import { ErrorCode } from '@ace-clublabs/payment-types';
import { CreateMembershipStatus, INewMembershipInfo } from '@lib/types';
import uuid4 from 'uuid4';
import { getNextPage } from '../../utils/getNextPage';
import { sources } from '../../api/sources';
import { cache, membershipInfoCache, memberTempCache } from '../../integrations/cache';
import { generateJoinURL } from '../../utils/generateJoinURL';
import CookieHelper from '../../utils/cookieHelper';
import {
  CouponStatus,
  CurrentPayPlan,
  FlowType,
  GetActiveMemberStatus,
  MembershipProductType,
  PriceCart,
  VoucherStatus,
} from '@ace-clublabs/common-membership-api-types';
import axios, { CancelTokenSource } from 'axios';
import { mapFromCreateMembershipStatusToCouponStatus, stripNonWhitelistedQueryParams } from '../../utils/helpers';
import LogCannon from '@ace-clublabs/log-cannon';

export interface AppStateProps {
  children?: React.ReactNode;
  useMockState?: boolean;
  mockState?: Partial<IMembershipAppState>;
  [key: string]: any;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

export const processBenefits = (additionalBenefits: IAdditionalBenefits[]) => {
  additionalBenefits.forEach((additionalBenefit, idx) => {
    if (additionalBenefit == null || !additionalBenefit?.group1 || !additionalBenefit?.valuePairs) return;

    const groups = [additionalBenefit.group1, additionalBenefit.group2].filter(Boolean);
    groups.forEach((_, i) => {
      for (var j = 0; j < additionalBenefit.valuePairs!.length; j++) {
        var replaceValue = additionalBenefit.valuePairs?.[j].value.text;
        if (additionalBenefit.valuePairs?.[j].value.isLink) {
          replaceValue =
            '<a href="' +
            additionalBenefit.valuePairs[j].value.link +
            '" target="_blank" style="color: #0156A3">' +
            additionalBenefit.valuePairs[j].value.text +
            '</a>';
        }
        groups[i].benefits.forEach((_, k) => {
          groups[i].benefits[k] = groups[i].benefits[k].replaceAll(
            '{{' + additionalBenefit.valuePairs?.[j].name + '}}',
            replaceValue!
          );
        });
      }

      groups[i].benefits.forEach((_, k) => {
        const hasRegistrationSymbol = groups[i].benefits[k].includes('®');
        if (hasRegistrationSymbol) {
          groups[i].benefits[k] = groups[i].benefits[k].replaceAll(
            '®',
            '<sup style="font-size: 0.6rem; vertical-align: text-bottom">®</sup>'
          );
        }
      });
    });
  });

  return additionalBenefits;
};

export const AppState: (props: AppStateProps) => JSX.Element = (props) => {
  const savedData = cache.get();

  const history = useHistory();
  const [couponAndCampaignCodeInfo, setCouponAndCampaignCodeInfo] = useState<{
    zipCode?: string | undefined | null;
    couponToken?: string | undefined | null;
    campaignCode?: string | undefined | null;
  }>({});

  const savedMembershipInfo = membershipInfoCache.get();
  const [membershipInfo, setMembershipInfo] = useState(savedMembershipInfo || (null as INewMembershipInfo | null));
  const [sessionData, setSessionData] = useState(savedData ?? getDefaultFormData());
  const [priceCart, setPriceCart] = useState<PriceCart | null>(null);
  const [priceCartCancelTokenSource, setPriceCartCancelTokenSource] = useState(null as null | CancelTokenSource);
  const [isPriceCartLoading, setPriceCartLoading] = useState(false);
  const { loadingInfo, loadingProvider } = useLoadingInfo({ isLoading: false, loadingDetails: [] });

  const getConfirmation: () => void = async () => {
    const request: IJoinConfirmationRequest = { sessionId: sessionData.sessionId ?? '' };
    const confirmationData = await sources.getConfirmation(request);
    if (confirmationData == null) {
      throw new Error('Confirmation not found');
    }
    const newCacheData: NewJoinSessionData = { ...sessionData, confirmationData };
    updateMembershipSession(newCacheData);
  };

  const updateMembershipSession = (data: NewJoinSessionData): void => {
    cache.set(data);
    setSessionData(data);
  };

  const updateMembershipForm = (formData: NewMembershipFormData) => {
    updateMembershipSession({
      ...sessionData,
      formData,
    });
  };

  const saveMembershipInfo = (info: INewMembershipInfo) => {
    membershipInfoCache.set(info);
    setMembershipInfo(info);
  };

  useEffect(() => {
    if (priceCart) {
      updateMembershipSession({ ...sessionData, priceCart });
    }
  }, [priceCart]);

  const updateAssociates = async (
    modification: 'add' | 'update' | 'remove',
    associate: AssociateInfo
  ): Promise<void> => {
    const associates = sessionData.formData.associates ?? [];
    let updatedAssociates: AssociateInfo[] | undefined = [...associates];

    switch (modification) {
      case 'add':
        updatedAssociates.push({ ...associate, virtualId: uuid4() });
        break;
      case 'remove':
        updatedAssociates = updatedAssociates.filter((a) => a.virtualId !== associate.virtualId);
        updatedAssociates = updatedAssociates.length !== 0 ? updatedAssociates : undefined;
        break;
      case 'update':
        updatedAssociates = updatedAssociates.map((a) => (a.virtualId === associate.virtualId ? associate : a));
        break;
    }

    const updatedFormData = {
      ...sessionData.formData,
      associates: updatedAssociates,
    };

    updateMembershipForm(updatedFormData);
    const costCalculatorResponse = await getNewBusinessCost(updatedFormData);

    if (costCalculatorResponse?.error && costCalculatorResponse.error?.status !== GetActiveMemberStatus.NOT_ACTIVE) {
      // remove updatedData
      updateMembershipForm(sessionData.formData);
      throw new Error('This associate is already an active member');
    }
  };

  const updateEnrollments = (modification: 'add' | 'remove', enrollment: Enrollments) => {
    let enrollments = new Set(sessionData.formData.enrollments ?? []);

    switch (modification) {
      case 'add':
        enrollments.add(enrollment);
        break;
      case 'remove':
        enrollments.delete(enrollment);
        break;
    }

    const updatedEnrollments = enrollments.size ? Array.from(enrollments) : undefined;

    const updatedFormData = {
      ...sessionData.formData,
      enrollments: updatedEnrollments,
    };

    updateMembershipForm(updatedFormData);
  };

  const updateBillPlan = (updatedBillPlan: CurrentPayPlan) => {
    const previousAnnualBillPlan =
      updatedBillPlan !== CurrentPayPlan.RECURRINGMONTHLY
        ? updatedBillPlan
        : sessionData.formData.previousAnnualBillPlan;

    const updatedFormData = {
      ...sessionData.formData,
      billPlan: updatedBillPlan,
      previousAnnualBillPlan,
    };

    updateMembershipForm(updatedFormData);
    getNewBusinessCost(updatedFormData);
  };

  const updatePaperlessOption = (paperlessOptOutType: PaperlessOptOutType, value: boolean) => {
    const updatedFormData = {
      ...sessionData.formData,
    };

    switch (paperlessOptOutType) {
      case PaperlessOptOutType.MAGAZINE:
        updatedFormData.declineMagazine = value;
        break;
      case PaperlessOptOutType.PAPERLESS_BILLING:
        updatedFormData.paperlessBilling = value;
        break;
      case PaperlessOptOutType.PHYSICAL_CARD:
        updatedFormData.declinePhysicalCard = value;
        break;
    }
    updateMembershipForm(updatedFormData);
  };

  const updateProducts = (modification: 'add' | 'remove', product: ProductType) => {
    let products = new Set(sessionData.formData.products ?? []);

    switch (modification) {
      case 'add':
        products.add(product);
        break;
      case 'remove':
        products.delete(product);
        break;
    }

    const updatedProducts = products.size ? Array.from(products) : undefined;

    const updatedFormData = {
      ...sessionData.formData,
      products: updatedProducts,
    };

    updateMembershipForm(updatedFormData);
    getNewBusinessCost(updatedFormData);
  };

  const routeMembershipStatus: (status: string) => Promise<void> = async (status) => {
    switch (status) {
      case CreateMembershipStatus.COMPLETED:
        await getConfirmation();
        loadingProvider.endLoading();
        history.push(Pages.ConfirmationPage);
        break;
      case CreateMembershipStatus.CHARGE_DECLINED:
        await getPaymentManifestId(ErrorCode.ChargeDeclined);
        break;
      case CreateMembershipStatus.CARD_NOT_AAA_VISA:
        await getPaymentManifestId(ErrorCode.CardMustBeAAAVisa);
        break;
      case CreateMembershipStatus.GROUPON_COUPON_REDEEMED:
      case CreateMembershipStatus.GROUPON_COUPON_EXPIRED:
      case CreateMembershipStatus.GROUPON_COUPON_REFUNDED:
        const newCacheData: NewJoinSessionData = {
          ...sessionData,
          tokenStatus: mapFromCreateMembershipStatusToCouponStatus(status),
        };
        updateMembershipSession(newCacheData);
        loadingProvider.endLoading();
        history.push(Pages.VoucherStatus);
        break;
      default:
        // fail case
        loadingProvider.endLoading();
        history.push(Pages.SorryPage);
    }
  };

  const submitMembershipForm = async (
    skipApiAddressValidation: boolean | undefined,
    skipDonorAddressValidation: boolean | undefined
  ): Promise<ISubmitNewJoinFormDataResponse> => {
    const request: ISubmitNewJoinFormDataRequest = {
      ...sessionData.formData,
      newJoinSessionId: sessionData?.sessionId ?? '',
      skipAddressValidation: skipApiAddressValidation ?? false,
      skipDonorAddressValidation: skipDonorAddressValidation ?? false,
      trafficSourceCategory: (global as any).utag?.data?.traffic_category || '',
      websiteSource: (global as any).utag?.data?.website_source || '',
      trafficSource: (global as any).utag?.data?.traffic_source || '',
    };

    const validateMembershipResponse = await sources.submitNewJoinFormData(request);
    return validateMembershipResponse;
  };

  const handleZipCodeChange = () => {
    const zipCode = sessionData.formData.residenceAddress.zipCode!;
    const flowType = sessionData.flowType;
    const offerToken = sessionData.offerToken;

    memberTempCache.set(sessionData);

    let redirectUrl = `${Pages.LandingPage}?zip=${zipCode}&campaigncode=${sessionData.campaignCode}`;
    if (flowType === FlowType.GROUPON) {
      const currentUrl = new URL(window.location.href);
      const urlSearchParams = new URL(`${currentUrl.origin}/${redirectUrl}`).searchParams;
      urlSearchParams.delete('campaigncode');
      urlSearchParams.append('flow', FlowType.GROUPON);
      urlSearchParams.append('couponcode', offerToken!);

      const params = urlSearchParams.toString();
      redirectUrl = `${Pages.LandingPage}?${params}`;
    }
    history.push(redirectUrl);

    loadingProvider.endLoading();
  };

  const checkMembershipStatus = async (): Promise<MembershipStatus> => {
    const request: ICheckMembershipStatusRequest = { sessionId: sessionData?.sessionId ?? '' };
    const response: MembershipStatus = await sources.checkMembershipStatus(request);
    if (response == null) {
      throw new Error('Invalid response from endpoint');
    }
    return response;
  };

  const getPaymentManifestId: (softError?: ErrorCode) => void = async (softError) => {
    loadingProvider.startLoading('Saving your membership information');

    const sessionId = sessionData.sessionId ?? '';
    const pageTag = sessionData.flowType === FlowType.GIFT ? 'membership:gift:payment' : 'payment';

    const tags = UtagHelper.buildTags(sessionData, membershipInfo!, pageTag);
    const declineMagazine = sessionData?.formData?.declineMagazine;
    const paperlessBilling = sessionData?.formData?.paperlessBilling;
    const declinePhysicalCard = sessionData?.formData?.declinePhysicalCard;
    const generateAutoLead = sessionData?.formData?.generateAutoLead;
    const hasCcOnlyCookie = CookieHelper.getCookie('tnt_pay_config') === 'cc-only';

    const request: IGetPaymentManifestRequest = {
      newJoinSessionId: sessionId,
      errorCode: softError,
      tags,
      declineMagazine,
      paperlessBilling,
      declinePhysicalCard,
      generateAutoLead,
      ...(hasCcOnlyCookie ? { ccOnlyCookie: 'cc-only' } : {}),
    };

    try {
      const response: IGetPaymentManifestResponse = await sources.getPaymentManifest(request);
      if (window.location.hostname === 'localhost') {
        history.push(`${Pages.CreateMembershipStatusPage}?id=${sessionData.sessionId}`);
      } else {
        window.location.assign(`https://${window.location.hostname}/payments/${response?.manifestId}`);
      }
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;

      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            getPaymentManifestId(softError);
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };

  const sendTag = (type: UtagType, page: string, overrides?: object) => {
    const tags = UtagHelper.buildTags(sessionData, membershipInfo, page, overrides);

    switch (type) {
      case UtagType.dispatch:
        UtagHelper.dispatchTntSpaEvent(tags);
        break;
      case UtagType.UDO:
      case UtagType.view:
        UtagHelper.dispatchTntSpaEvent(tags);
        UtagHelper.view(tags);
        break;
      default:
        UtagHelper.link(tags);
        break;
    }
  };

  const confirmToken: (
    zipcode: string,
    token: string,
    campaigncode: string,
    flowtype: string,
    empId: string
  ) => Promise<TokenResponse> = async (zipcode, token, campaigncode, flow, empId) => {
    const response: IApiResponse = await sources.getJoinInitInfo({
      zipcode,
      couponcode: token,
      campaigncode,
      flow,
      empId,
    });
    if (response != null) {
      if (response.statusCode == 200)
        return {
          sessionId: response?.data?.cacheId,
          tokenStatus: response?.data?.tokenStatus,
          flowType: response?.data?.flowType,
          statusCode: response.statusCode,
          statusDescription: response.statusDescription,
        } as TokenResponse;
      else
        return {
          sessionId: response?.data?.cacheId,
          tokenStatus: response?.error?.tokenStatus,
          flowType: response?.data?.flowType,
          statusCode: response.statusCode,
          statusDescription: response.statusDescription,
          campaignCode: response?.data?.campaignCode,
        } as TokenResponse;
    } else {
      throw new Error('Invalid token response');
    }
  };

  const redirectToOLM1 = (zipCode: string, campaignCode: string, clubCode?: string) => {
    clubCode = clubCode ?? CookieHelper.getClubCodeFromZipcodeCookie();
    const joinUrl = generateJoinURL(clubCode, zipCode == null ? '' : zipCode, campaignCode);
    return window.location.assign(joinUrl);
  };

  const initializeByZipCouponCampaignCodeAndFlowType: (
    zipCode: string | null,
    couponToken: string | null,
    campaignCode: string | null,
    flowType: string | null,
    queryString: string | null,
    empId: string | null
  ) => void = async (zipCode, couponCode, campaignCode, flowType, queryString, empId) => {
    const args = [zipCode, couponCode, campaignCode, flowType, queryString, empId] as const;
    try {
      loadingProvider.startLoading('Starting your membership');
      setCouponAndCampaignCodeInfo({ zipCode, couponToken: couponCode, campaignCode });
      const tokenResponse: TokenResponse = await confirmToken(
        zipCode ?? '',
        couponCode ?? '',
        campaignCode ?? '',
        flowType ?? '',
        empId ?? ''
      );

      if (flowType === FlowType.GROUPON && tokenResponse?.statusCode === 403) {
        throw new Error('Groupon flow is disabled');
      }

      const invalidCampaignCode = tokenResponse?.statusCode == 403;
      if (invalidCampaignCode) {
        //Redirect to OLM 1.0
        redirectToOLM1(zipCode!, campaignCode!);
        return;
      }

      campaignCode = flowType === FlowType.GROUPON ? (tokenResponse?.campaignCode ?? campaignCode) : campaignCode;
      const defaultCache = {
        ...getDefaultFormData(
          zipCode,
          couponCode,
          campaignCode,
          tokenResponse.flowType,
          tokenResponse.statusDescription
        ),
        sessionId: tokenResponse.sessionId ?? null,
        tokenStatus: flowType === FlowType.GROUPON ? tokenResponse?.tokenStatus : null,
      };

      defaultCache.queryString = queryString;

      updateMembershipSession(defaultCache);

      if (
        couponCode != null &&
        couponCode != '' &&
        tokenResponse.tokenStatus !== CouponStatus.Active &&
        tokenResponse.tokenStatus !== VoucherStatus.Available
      ) {
        throw new Error('Token is not active');
      }

      loadingProvider.addLoadingDetail('Finalizing a few things');
      if (tokenResponse.sessionId == null) {
        throw new Error('No session found');
      }

      const membershipInfoResponse: IGetNewMembershipInfoResponse = await getMembershipInfo(tokenResponse.sessionId);

      const invalidClubCode = membershipInfoResponse?.error?.code === ErrorType.CLUB_CODE_NOT_ACCEPTED;
      if (invalidClubCode) {
        const clubCode = membershipInfoResponse?.error?.data?.clubCode;
        redirectToOLM1(zipCode!, campaignCode!, clubCode);
        return;
      }

      if (membershipInfoResponse.statusCode != null && membershipInfoResponse.statusCode != 200) {
        defaultCache.statusDescription = membershipInfoResponse.statusDescription;
        updateMembershipSession(defaultCache);
        throw new Error('Membership configuration not found');
      }
      saveMembershipInfo(membershipInfoResponse);

      defaultCache.formData.billPlan = membershipInfoResponse.requestedBillPlan;
      defaultCache.formData.giftDetails = membershipInfoResponse.giftDetails;
      defaultCache.region = membershipInfoResponse?.region;
      defaultCache.clubCode = membershipInfoResponse?.clubCode;
      defaultCache.campaignCode = membershipInfoResponse.campaignCode ?? campaignCode;
      defaultCache.enableGenerateLead = membershipInfoResponse?.enableGenerateLead;
      if (defaultCache.enableGenerateLead && defaultCache.formData.generateAutoLead === undefined) {
        defaultCache.formData.generateAutoLead = true;
      }

      //Call NewJoinGetContent API here to get green box messages, additional benefits and disclaimers and save in cache
      if (defaultCache.clubCode && defaultCache.campaignCode && defaultCache.region) {
        const membershipContentResponse = await getMembershipContent(tokenResponse.sessionId);

        const { greenBoxMessage, additionalBenefits, disclaimerMessage } = membershipContentResponse.data;

        defaultCache.membershipContent = {
          greenBoxMessage: greenBoxMessage ?? null,
          additionalBenefits: processBenefits(additionalBenefits),
          disclaimerMessage: processDisclaimerMessage(disclaimerMessage),
        };
      } else {
        throw new Error('Membership content request not complete');
      }

      //Add paperless options to session formData here. This handles initialization
      const physicalCard: IPaperlessOptOut | undefined = membershipInfoResponse?.paperlessOptOuts?.find(
        (p) => p.type === 'PHYSICAL_CARD'
      );
      const paperless: IPaperlessOptOut | undefined = membershipInfoResponse?.paperlessOptOuts?.find(
        (p) => p.type === 'PAPERLESS_BILLING'
      );
      const magazine: IPaperlessOptOut | undefined = membershipInfoResponse?.paperlessOptOuts?.find(
        (p) => p.type === 'MAGAZINE'
      );

      //By default
      defaultCache.formData.declineMagazine = magazine ? magazine.selected : true;
      defaultCache.formData.paperlessBilling = paperless ? paperless.selected : true;
      defaultCache.formData.declinePhysicalCard = physicalCard ? physicalCard.selected : true;

      updateMembershipSession(defaultCache);

      loadingProvider.endLoading();
      const currentUrl = window.location.href;
      const urlSearchParams = new URL(currentUrl).searchParams;
      const params = urlSearchParams.toString();
      const urlWithWhiteListedParams = stripNonWhitelistedQueryParams(currentUrl, params);
      window.history.replaceState(null, '', urlWithWhiteListedParams);

      const benefitsURI = `${getNextPage(Pages.LandingPage, tokenResponse.flowType)}?${params}`;
      history.push(benefitsURI);
      return;
    } catch (e: any) {
      loadingProvider.endLoading();
      const isBotDetectionResponse = e?.response?.status === 403 && !!e.response?.data?.pxEvent;
      const isMaintenancePageResponse = e?.error?.response?.status === 503;
      if (isMaintenancePageResponse) {
        history.push(Pages.MaintenancePage);
      } else if (isBotDetectionResponse) {
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            initializeByZipCouponCampaignCodeAndFlowType(...args);
          }
        };
      } else {
        if (flowType === FlowType.GROUPON && e.message === 'Token is not active') history.push(Pages.VoucherStatus);
        else history.push(Pages.SorryPage);
      }
    }
  };

  const processDisclaimerMessage = (disclaimerMessage: IDisclaimers) => {
    if (disclaimerMessage == null || disclaimerMessage.sections == null || disclaimerMessage.valuePairs == null)
      return disclaimerMessage;

    for (var i = 0; i < disclaimerMessage.sections.length; i++) {
      for (var j = 0; j < disclaimerMessage.valuePairs.length; j++) {
        var replaceValue = disclaimerMessage.valuePairs[j].value.text;
        if (disclaimerMessage.valuePairs[j].value.isLink) {
          replaceValue =
            '<a href="' +
            disclaimerMessage.valuePairs[j].value.link +
            '" target="_blank" style="color: #0156A3">' +
            disclaimerMessage.valuePairs[j].value.text +
            '</a>';
        }
        disclaimerMessage.sections[i].value = disclaimerMessage.sections[i].value.replaceAll(
          '{{' + disclaimerMessage.valuePairs[j].name + '}}',
          replaceValue
        );
      }
    }

    return disclaimerMessage;
  };

  const getMembershipInfo: (sessionId: string) => Promise<INewMembershipInfo> = async (sessionId) => {
    const response: IGetNewMembershipInfoResponse = await sources.getNewMembershipInfo({ sessionId });

    if (response == null) {
      throw new Error('Membership configuration not found');
    }
    saveMembershipInfo(response);
    return response;
  };

  // to be refactored at least in a naming way
  const refreshSession = (sessionId?: string | null): void => {
    window.scrollTo(0, 0);
    if (membershipInfo?.benefits != null) {
      return loadingProvider.endLoading();
    }

    loadingProvider.startLoading('Finalizing membership details');
    if (savedData?.sessionId != null) {
      setSessionData(savedData);
    }

    getMembershipInfo(savedData?.sessionId ?? '')
      .then(() => {
        loadingProvider.endLoading();
      })
      .catch((e) => {
        loadingProvider.endLoading();
        const isBotDetectionResponse = e?.response?.status === 403 && !!e.response?.data?.pxEvent;
        if (isBotDetectionResponse) {
          window._pxOnCaptchaSuccess = function (isValid) {
            if (isValid) {
              refreshSession();
            }
          };
        } else {
          history.push(Pages.SorryPage);
        }
      });
  };

  const setStep = (step: string): void => {
    const newSessionData = { ...sessionData, step: step };
    updateMembershipSession(newSessionData);
  };

  const registerIdentityAccount = async (
    sessionId: string,
    email: string,
    password: string
  ): Promise<IRegisterAccountResponse> => {
    loadingProvider.startLoading('Creating your account');
    await sleep(500);
    const request: IRegisterAccountRequest = { sessionId, email, password };
    const registrationResponse = await sources.registerAccount(request);

    if (registrationResponse.success === true) {
      loadingProvider.endLoading();
      updateMembershipSession({
        ...sessionData,
        completedRegistration: true,
        partnerOffers: {
          availableOffers: null,
          enrollmentStatus: null,
          enrollmentEmail: email,
          partnerStatuses: null,
        },
      });
      return { success: true };
    } else {
      loadingProvider.endLoading();
      return registrationResponse;
    }
  };

  const getNewBusinessCost = async (updatedFormData?: NewMembershipFormData): Promise<IGetNewBusinessCostResponse> => {
    const formData = updatedFormData ?? sessionData.formData;

    const request: IGetNewBusinessCostRequest = {
      membershipLevel: formData.membershipLevel ?? membershipInfo?.requestedMembershipLevel!,
      billPlan: formData.billPlan!,
      newJoinSessionId: sessionData?.sessionId!,
      additionalMembers: formData?.associates ?? [],
      products: (formData?.products as unknown[] as MembershipProductType[]) ?? [], // this is stupid
    };

    setPriceCartLoading(true);

    if (priceCartCancelTokenSource) {
      priceCartCancelTokenSource.cancel('Canceling price cart update');
    }

    const source = axios.CancelToken.source();
    setPriceCartCancelTokenSource(() => source);
    const cancelToken = source.token;

    const priceCart = await sources.getNewBusinessCost(request, cancelToken);
    setPriceCartCancelTokenSource(null);
    if (priceCart?.items) {
      setPriceCart(priceCart as PriceCart);
    }

    setPriceCartLoading(false);
    return priceCart;
  };

  const getMembershipContent = async (newJoinSessionId: string): Promise<IGetMembershipContentResponse> => {
    const response: IGetMembershipContentResponse = await sources.getMembershipContent({
      newJoinSessionId,
    });
    if (!response) {
      throw new Error('Invalid response from endpoint');
    }
    return response;
  };

  const fetchPartnerOffers = async (sessionId: string, email: string): Promise<IPartnerIntegration[]> => {
    const callPartnerEligibilityApi = async () => {
      try {
        const { partners } = await sources.getPartnerEligibility({
          sessionId,
          email,
        });
        return partners;
      } catch (e) {
        console.error(e);
        return Promise.resolve([]);
      }
    };

    const availableOffers = sessionData.partnerOffers?.availableOffers;
    if (availableOffers && availableOffers.length > 0) {
      return availableOffers;
    }
    const partners = await callPartnerEligibilityApi();

    const updatedSession: NewJoinSessionData = {
      ...sessionData,
      partnerOffers: {
        availableOffers: partners,
        enrollmentStatus: null,
        enrollmentEmail: email,
        partnerStatuses: null,
      },
      formData: {
        ...sessionData.formData,
      },
    };

    updateMembershipSession(updatedSession);
    await sleep(100);

    return partners;
  };

  return (
    <MembershipAppStateContext.Provider
      value={{
        loadingInfo,
        sessionData,
        loadingProvider,
        membershipInfo,
        isPriceCartLoading,
        setZipCouponCampaignCodeAndFlowType: initializeByZipCouponCampaignCodeAndFlowType,
        refreshSession,
        submitMembershipForm,
        getPaymentManifestId,
        setStep,
        checkMembershipStatus,
        getConfirmation,
        registerIdentityAccount,
        sendTag,
        routeMembershipStatus,
        updateAssociates,
        updateEnrollments,
        updateProducts,
        updateBillPlan,
        updatePaperlessOption,
        updateMembershipForm,
        getNewBusinessCost,
        getMembershipContent,
        handleZipCodeChange,
        updateMembershipSession,
        fetchPartnerOffers,
        sleep,
        ...(props.useMockState && props.mockState ? props.mockState : {}),
      }}
    >
      {props.children}
    </MembershipAppStateContext.Provider>
  );
};
